/**
 * Просмотр юридического документа: оглавление, разделы с якорями, дата
 * действующей редакции. Один компонент для экрана A-09, для листа с
 * документом на шаге согласий A-05 и для блокирующего экрана SYS-05.
 */
import { useId } from 'react'

import type { LegalDocument as LegalDocumentId } from '@shared/api/types'
import { Icon } from '@shared/ui'

import { useAppStore } from '../../model/app-store'
import { LEGAL_TEXTS, type LegalText } from './documents'
import styles from './legal.module.css'

function formatVersion(version: string | undefined): string | null {
  if (!version) return null
  const date = new Date(version)
  if (Number.isNaN(date.getTime())) return version
  return new Intl.DateTimeFormat('ru', { day: 'numeric', month: 'long', year: 'numeric' }).format(date)
}

export function LegalDocument({
  id,
  compact = false,
}: {
  id: LegalDocumentId
  compact?: boolean
}): React.ReactElement {
  const config = useAppStore((s) => s.config)
  const text: LegalText = LEGAL_TEXTS[id]
  const version = formatVersion(config?.legal?.[id]?.version)
  const prefix = useId()

  return (
    <article className={styles['document']} aria-labelledby={`${prefix}-title`}>
      <header className={styles['head']}>
        {!compact ? (
          <h1 id={`${prefix}-title`} className={styles['title']}>
            {text.title}
          </h1>
        ) : (
          <h2 id={`${prefix}-title`} className={styles['titleCompact']}>
            {text.title}
          </h2>
        )}
        <p className={styles['summary']}>{text.summary}</p>
        {version ? (
          <p className={styles['version']}>
            <Icon name="document" size={15} />
            Действующая редакция от {version}
          </p>
        ) : null}
      </header>

      <nav className={styles['toc']} aria-label="Содержание">
        <ol>
          {text.sections.map((section) => (
            <li key={section.id}>
              <a href={`#${prefix}-${section.id}`}>{section.title}</a>
            </li>
          ))}
        </ol>
      </nav>

      {text.sections.map((section) => (
        <section key={section.id} id={`${prefix}-${section.id}`} className={styles['section']}>
          <h2 className={styles['sectionTitle']}>{section.title}</h2>
          <SectionBody paragraphs={section.paragraphs} />
        </section>
      ))}
    </article>
  )
}

/** Абзацы и списки: подряд идущие строки «— …» собираются в один список. */
function SectionBody({ paragraphs }: { paragraphs: readonly string[] }): React.ReactElement {
  const blocks: React.ReactNode[] = []
  let list: string[] = []

  const flush = (): void => {
    if (!list.length) return
    blocks.push(
      <ul key={`list-${blocks.length}`} className={styles['list']}>
        {list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>,
    )
    list = []
  }

  for (const paragraph of paragraphs) {
    if (paragraph.startsWith('— ')) {
      list.push(paragraph.slice(2))
      continue
    }
    flush()
    blocks.push(
      <p key={paragraph.slice(0, 40) + blocks.length} className={styles['paragraph']}>
        {paragraph}
      </p>,
    )
  }
  flush()

  return <>{blocks}</>
}
